/**
 * TypeScript interfaces for the onboarding system
 * Matches the user_onboarding table structure and provides type safety
 */

// Onboarding status enum
export enum OnboardingStatus {
  NOT_STARTED = 'not_started',
  AWAITING_CONFIRMATION = 'awaiting_confirmation',
  NEEDS_ATTENTION = 'needs_attention',
  COMPLETE = 'complete'
}

// Setup method enum for integrations
export enum SetupMethod {
  DIY = 'diy',
  REMOTE_ACCESS = 'remote_access'
}

// Available onboarding sections
export enum OnboardingSection {
  BUSINESS_DETAILS = 'business_details',
  STRIPE_CONNECT = 'stripe_connect',
  PRINTER = 'printer',
  DOMAIN = 'domain',
  WEBSITE = 'website',
  GOOGLE_BUSINESS = 'google_business',
  AGGREGATORS = 'aggregators'
}

// Main user onboarding data interface matching the database table
export interface UserOnboardingData {
  id: string;
  user_id: string;
  organisation_id?: string;
  restaurant_id?: string;
  created_at: string;
  updated_at: string;

  // Overall status
  onboarding_status: OnboardingStatus;
  
  // Business Details
  restaurant_name?: string;
  organisation_name?: string;
  template_restaurant_name?: string;
  address?: string;
  email?: string;
  phone?: string;
  contact_person?: string;
  nzbn?: string;
  company_name?: string;
  trading_name?: string;
  director_name?: string;
  director_mobile_number?: string;
  gst_number?: string;
  venue_operating_hours?: string;
  default_preparation_time_minutes?: number;
  
  // Social Media
  facebook_url?: string;
  instagram_url?: string;
  google_review_url?: string;
  
  // Delivery & Branding Settings
  ubereats_markup?: number;
  primary_color?: string;
  secondary_color?: string;
  
  // Stripe Connect
  stripe_connect_link?: string;
  stripe_connection_status?: OnboardingStatus;
  
  // Printer Setup
  printer_setup_method?: SetupMethod;
  printer_setup_status?: OnboardingStatus;
  teamviewer_id?: string;
  teamviewer_password?: string;
  
  // Domain Setup
  dns_setup_method?: SetupMethod;
  dns_status?: OnboardingStatus;
  root_domain?: string;
  custom_sub_domain?: string;
  review_management_subdomain?: string;
  ordering_subdomain_ip?: string;
  review_management_ip?: string;
  dns_provider_username?: string;
  dns_provider_password?: string;
  dns_provider_name?: string;
  dns_provider_custom_name?: string;
  nameserver_1?: string;
  nameserver_2?: string;
  
  // Website Integration
  website_setup_method?: SetupMethod;
  website_linked_status?: OnboardingStatus;
  website_login_username?: string;
  website_login_password?: string;
  website_platform?: string;
  website_platform_custom?: string;
  
  // Google Business Profile
  google_setup_method?: SetupMethod;
  google_ordering_status?: OnboardingStatus;
  
  // Order Aggregators
  uber_aggregation_status?: OnboardingStatus;
  uber_setup_method?: SetupMethod;
  ubereats_store_id?: string;
  doordash_aggregation_status?: OnboardingStatus;
  doordash_setup_method?: SetupMethod;
  doordash_login?: string;
  doordash_pin?: string;
  
  // Business details section status
  business_details_status?: OnboardingStatus;
}

// Section progress tracking
export interface SectionProgress {
  section: OnboardingSection;
  status: OnboardingStatus;
  isRequired: boolean;
  hasData: boolean;
  validationErrors?: string[];
}

// Overall progress summary
export interface OnboardingProgress {
  overallStatus: OnboardingStatus;
  completedSections: number;
  totalSections: number;
  progressPercentage: number;
  sections: SectionProgress[];
}

// Validation rules for each section
export interface ValidationRules {
  [OnboardingSection.BUSINESS_DETAILS]: {
    required: (keyof UserOnboardingData)[];
    optional: (keyof UserOnboardingData)[];
  };
  [OnboardingSection.STRIPE_CONNECT]: {
    required: (keyof UserOnboardingData)[];
    optional: (keyof UserOnboardingData)[];
  };
  [OnboardingSection.PRINTER]: {
    required: (keyof UserOnboardingData)[];
    optional: (keyof UserOnboardingData)[];
  };
  [OnboardingSection.DOMAIN]: {
    required: (keyof UserOnboardingData)[];
    optional: (keyof UserOnboardingData)[];
  };
  [OnboardingSection.WEBSITE]: {
    required: (keyof UserOnboardingData)[];
    optional: (keyof UserOnboardingData)[];
  };
  [OnboardingSection.GOOGLE_BUSINESS]: {
    required: (keyof UserOnboardingData)[];
    optional: (keyof UserOnboardingData)[];
  };
  [OnboardingSection.AGGREGATORS]: {
    required: (keyof UserOnboardingData)[];
    optional: (keyof UserOnboardingData)[];
  };
}

// Form data interfaces for each section
export interface BusinessDetailsFormData {
  restaurant_name: string;
  organisation_name: string;
  address: string;
  email: string;
  phone: string;
  contact_person: string;
  nzbn?: string;
  company_name?: string;
  trading_name?: string;
  director_name?: string;
  director_mobile_number?: string;
  gst_number?: string;
  venue_operating_hours?: string;
  default_preparation_time_minutes?: number;
  facebook_url?: string;
  instagram_url?: string;
  google_review_url?: string;
  ubereats_markup?: number;
  primary_color?: string;
  secondary_color?: string;
}

export interface PrinterSetupFormData {
  printer_setup_method: SetupMethod;
  teamviewer_id?: string;
  teamviewer_password?: string;
}

export interface DomainSetupFormData {
  dns_setup_method: SetupMethod;
  root_domain?: string;
  custom_sub_domain?: string;
  review_management_subdomain?: string;
  dns_provider_username?: string;
  dns_provider_password?: string;
  dns_provider_name?: string;
  dns_provider_custom_name?: string;
}

export interface WebsiteIntegrationFormData {
  website_setup_method: SetupMethod;
  website_login_username?: string;
  website_login_password?: string;
  website_platform?: string;
  website_platform_custom?: string;
}

export interface GoogleBusinessFormData {
  google_setup_method: SetupMethod;
  google_review_url?: string;
}

export interface AggregatorsFormData {
  aggregators_setup_method: SetupMethod;
}

// API response interfaces
export interface OnboardingUpdateRequest {
  section: OnboardingSection;
  data: Partial<UserOnboardingData>;
}

export interface OnboardingUpdateResponse {
  success: boolean;
  data?: UserOnboardingData;
  error?: string;
}

// Section configuration for dynamic rendering
export interface SectionConfig {
  id: OnboardingSection;
  title: string;
  description: string;
  icon: string;
  isRequired: boolean;
  statusField: keyof UserOnboardingData;
  setupMethodField?: keyof UserOnboardingData;
}

// Constants
export const VALIDATION_RULES: ValidationRules = {
  [OnboardingSection.BUSINESS_DETAILS]: {
    required: [
      'restaurant_name',
      'organisation_name', 
      'address',
      'email',
      'phone',
      'contact_person',
      'nzbn',
      'company_name',
      'trading_name',
      'director_name',
      'director_mobile_number',
      'gst_number'
    ],
    optional: [
      'venue_operating_hours',
      'default_preparation_time_minutes',
      'facebook_url',
      'instagram_url',
      'google_review_url',
      'ubereats_markup',
      'primary_color',
      'secondary_color'
    ]
  },
  [OnboardingSection.STRIPE_CONNECT]: {
    required: [],
    optional: ['stripe_connect_link']
  },
  [OnboardingSection.PRINTER]: {
    required: ['printer_setup_method'],
    optional: ['teamviewer_id', 'teamviewer_password']
  },
  [OnboardingSection.DOMAIN]: {
    required: ['dns_setup_method'],
    optional: ['root_domain', 'custom_sub_domain', 'review_management_subdomain', 'ordering_subdomain_ip', 'review_management_ip', 'dns_provider_username', 'dns_provider_password', 'dns_provider_name', 'dns_provider_custom_name', 'nameserver_1', 'nameserver_2']
  },
  [OnboardingSection.WEBSITE]: {
    required: ['website_setup_method'],
    optional: ['website_login_username', 'website_login_password', 'website_platform', 'website_platform_custom']
  },
  [OnboardingSection.GOOGLE_BUSINESS]: {
    required: ['google_setup_method'],
    optional: ['google_review_url']
  },
  [OnboardingSection.AGGREGATORS]: {
    required: [],
    optional: ['uber_aggregation_status', 'uber_setup_method', 'ubereats_store_id', 'doordash_aggregation_status', 'doordash_setup_method', 'doordash_login', 'doordash_pin']
  }
};

export const SECTION_CONFIGS: SectionConfig[] = [
  {
    id: OnboardingSection.BUSINESS_DETAILS,
    title: 'Business Details',
    description: 'Review and complete your business information',
    icon: 'building',
    isRequired: true,
    statusField: 'business_details_status'
  },
  {
    id: OnboardingSection.STRIPE_CONNECT,
    title: 'Payment Processing',
    description: 'Set up Stripe Connect for payment processing',
    icon: 'credit-card',
    isRequired: true,
    statusField: 'stripe_connection_status'
  },
  {
    id: OnboardingSection.PRINTER,
    title: 'Printer Setup',
    description: 'Configure your receipt printer',
    icon: 'printer',
    isRequired: true,
    statusField: 'printer_setup_status',
    setupMethodField: 'printer_setup_method'
  },
  {
    id: OnboardingSection.DOMAIN,
    title: 'Custom Domain',
    description: 'Set up your custom domain',
    icon: 'globe',
    isRequired: true,
    statusField: 'dns_status',
    setupMethodField: 'dns_setup_method'
  },
  {
    id: OnboardingSection.WEBSITE,
    title: 'Website Integration',
    description: 'Integrate with your existing website',
    icon: 'laptop',
    isRequired: true,
    statusField: 'website_linked_status',
    setupMethodField: 'website_setup_method'
  },
  {
    id: OnboardingSection.GOOGLE_BUSINESS,
    title: 'Google My Business',
    description: 'Connect your Google Business Profile',
    icon: 'map-pin',
    isRequired: true,
    statusField: 'google_ordering_status',
    setupMethodField: 'google_setup_method'
  },
  {
    id: OnboardingSection.AGGREGATORS,
    title: 'Order Aggregators',
    description: 'Set up UberEats and DoorDash integration',
    icon: 'truck',
    isRequired: false,
    statusField: 'uber_aggregation_status'
  }
];